"use client";

/**
 * Preferencia de modo claro/oscuro de la consola.
 *
 * La regla que ordena el white-label: **el color lo elige la liga, el modo lo elige
 * quien mira**. En la vitrina del hincha coinciden porque es la casa de la liga; en la
 * consola se separan — `/app` es nuestra herramienta, no la vitrina del cliente, pero
 * quien carga resultados a las tres de la tarde en una cancha con sol no está en las
 * mismas condiciones que quien cierra la fecha de noche.
 *
 * **Vive en `localStorage`, no en el backend**, por tres razones: es preferencia de
 * DISPOSITIVO y no de cuenta (claro en el notebook de la cancha, oscuro en el
 * escritorio); la app es static export, así que la lectura es de cliente igual; y
 * meterlo en el backend implica endpoint, migración y un round-trip antes de poder
 * pintar, para algo que se cambia una vez.
 *
 * Contrasta con `AdminScopeProvider`, que decidió lo contrario para el alcance («la URL
 * es la verdad»). No es inconsistencia: el alcance es contexto **compartible** —se manda
 * un link y el otro ve lo mismo—; el tema es preferencia **personal** y no debe viajar
 * en un link.
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "ld-tema";

export type ModoTema = "dark" | "light" | "system";
export type ModoResuelto = "dark" | "light";

function esModo(v: unknown): v is ModoTema {
  return v === "dark" || v === "light" || v === "system";
}

/** Lectura defensiva: storage inaccesible o valor corrupto ⇒ «system». */
export function leerModo(): ModoTema {
  try {
    const raw = window.localStorage.getItem(KEY);
    return esModo(raw) ? raw : "system";
  } catch {
    // Storage bloqueado (modo privado restrictivo): se sigue en memoria.
    return "system";
  }
}

function escribirModo(m: ModoTema): void {
  try {
    // «system» BORRA la preferencia en vez de guardarla: así vuelve a seguir al SO en
    // vivo, y no queda un valor guardado que haya que interpretar.
    if (m === "system") window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, m);
  } catch {
    // Igual que arriba: la elección vale para la sesión aunque no persista.
  }
}

/**
 * Suscriptores vivos del hook.
 *
 * Sin esto cada `useModoTema()` tiene su propio `useState` y el cambio se queda en el
 * componente que lo disparó: `ThemeToggle` se pintaba como seleccionado pero `AdminTheme`
 * —que es quien escribe el `data-theme`— nunca se enteraba, así que el modo solo cambiaba
 * al recargar. El listener de `storage` no alcanza: **no dispara en la pestaña que
 * escribe**, solo en las otras.
 */
const suscriptores = new Set<(m: ModoTema) => void>();

function prefiereOscuro(): boolean {
  try {
    if (typeof window.matchMedia !== "function") return true;
    return !window.matchMedia("(prefers-color-scheme: light)").matches;
  } catch {
    return true;
  }
}

/** Precedencia: preferencia guardada → `prefers-color-scheme` → oscuro (marca base). */
export function resolverModo(modo: ModoTema, sistemaClaro: boolean): ModoResuelto {
  if (modo !== "system") return modo;
  return sistemaClaro ? "light" : "dark";
}

export function useModoTema(): {
  modo: ModoTema;
  resuelto: ModoResuelto;
  setModo: (m: ModoTema) => void;
} {
  // El primer render tiene que coincidir con el HTML pre-renderizado (static export),
  // así que arranca en el default y la preferencia se aplica en el efecto. Si se leyera
  // el storage acá, React avisaría por mismatch de hidratación.
  const [modo, setModoState] = useState<ModoTema>("system");
  const [sistemaClaro, setSistemaClaro] = useState(false);

  useEffect(() => {
    setModoState(leerModo());
    setSistemaClaro(!prefiereOscuro());
  }, []);

  // Propagación dentro de la misma pestaña: cualquier consumidor que cambie el modo
  // los actualiza a todos, sin provider de por medio.
  useEffect(() => {
    suscriptores.add(setModoState);
    return () => {
      suscriptores.delete(setModoState);
    };
  }, []);

  // Con «Sistema» activo, cambiar el tema del SO cambia la consola sin recargar.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    // Envuelto: si matchMedia lanza (entornos restringidos), la consola tiene que
    // seguir pintándose en el modo por defecto, no reventar el árbol.
    try {
      const query = window.matchMedia("(prefers-color-scheme: light)");
      const update = () => setSistemaClaro(query.matches);
      update();
      if (typeof query.addEventListener === "function") {
        query.addEventListener("change", update);
        return () => query.removeEventListener("change", update);
      }
      query.addListener(update);
      return () => query.removeListener(update);
    } catch {
      return;
    }
  }, []);

  // Sincronía entre pestañas, igual que ya hace la sesión de auth.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== KEY) return;
      setModoState(leerModo());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setModo = useCallback((m: ModoTema) => {
    escribirModo(m);
    for (const avisar of suscriptores) avisar(m);
  }, []);

  return { modo, resuelto: resolverModo(modo, sistemaClaro), setModo };
}

export default useModoTema;
